/**
 * The Provider Interface type catalog + its two constrained axes (ADR-040 §2.4 /
 * §2.5; storyboard Flow V Frame 4).
 *
 * Pure, React-free. Mirrors the framework's **static** type registry
 * (:file:`packages/meridian_provider_framework/src/meridian_provider_framework/_registry.py`)
 * so the operator picking a type sees exactly the seven descriptors the service
 * can realize. The registry is not on the OpenAPI surface — the BFF validates
 * ``type_id`` against it server-side but never publishes it — so this is a
 * hand-maintained mirror, and a type added there must be added here too. The
 * consequence of forgetting is bounded and loud: the new type is simply not
 * offered, never mis-offered (the BFF rejects an unregistered ``type_id`` with a
 * 422 regardless).
 *
 * # Two constrained axes, where the egress twin has one
 *
 * Flow W constrains ``transport_security`` by type. Ingress constrains **both**
 * ``trust_mode`` (*who authenticates the counterparty*) and
 * ``transport_security`` (*what the wire does*), and REQ-UI-161 applies the same
 * absent-not-disabled rule to each: a value the type does not permit is **absent
 * from the control with the reason stated**, and where only one value is
 * permitted the field renders as a **stated fact** rather than a one-option
 * select. A select with nothing to select is indistinguishable from a broken
 * control, and a greyed-out option is a claim about permissions that is simply
 * false when the truth is that the type fixes it.
 *
 * ⚠ **`tls` appears in no ingress type's permitted set**, and that is the
 * registry's own reasoning rather than an omission here: a server-authenticated
 * wire verifies no client and so attributes nothing, which on an ingress
 * perimeter is a wire with no identity story. The presentation table below still
 * carries `tls` because a **stored** value outside its type's set is stated
 * rather than corrected (REQ-UI-161) — the surface must be able to render one.
 *
 * Cross-references:
 *   - ADR-040 §2.4 (the seven types), §2.5 (trust mode), §D-4 (per-interface trust),
 *     §D-6 (the allow-all filter).
 *   - ADR-023 (the tunnel delegation); ADR-013 (perimeter-only authorization).
 *   - REQ-PROVIDER-005 / -006 / -028 / -029 / -040; REQ-UI-161 / -162.
 *   - Storyboard Flow V (docs/engineering/detail/storyboards/flows/V-configure-provider-integration).
 */
import type { components } from "@/lib/api-types.gen";

/** Who opens the connection — ``listen`` is the ingress default (ADR-040 §2.5, V-2). */
export type ProviderConnectionRole = components["schemas"]["ProviderConnectionRole"];
/** Whether a configured entity is carrying data (ADR-040; shared with egress). */
export type InterfaceState = components["schemas"]["InterfaceState"];
/** What the wire does (REQ-PROVIDER-029): plaintext | tls | mtls. */
export type TransportSecurity = components["schemas"]["TransportSecurity"];
/** How an admitted connection is *attributed* (REQ-PROVIDER-028): mtls | tunnel. */
export type TrustMode = components["schemas"]["TrustMode"];
/** Which decoder an interface trusts (REQ-PROVIDER-040): legacy | engine. */
export type ConversionMode = components["schemas"]["ConversionMode"];

/**
 * One registered ingress interface type, as the operator picks it.
 *
 * The two ``allowed*`` tuples are the load-bearing fields: every conditional in
 * the editor keys off them rather than off ``typeId``, so a type whose
 * constraints change needs no change to the form.
 */
export interface ProviderInterfaceTypeDescriptor {
  /** Stable public id sent as ``type_id`` (e.g. ``"cds-ingress"``). */
  readonly typeId: string;
  /** Operator-facing label. */
  readonly label: string;
  /** The inbound listener this type binds by default. */
  readonly transportId: string;
  /** ``sapient`` or ``raw`` — what the interface carries. */
  readonly payloadDomain: "sapient" | "raw";
  /** One-line operator description (the registry's, in operator register). */
  readonly description: string;
  /**
   * Which way data moves (REQ-PROVIDER-005). A property of the **type**, never
   * of the row — an interface carrying its own direction could contradict its
   * own ``type_id`` and nothing would catch the disagreement.
   *
   * Not to be confused with {@link ProviderConnectionRole}, which says who
   * *opens* the connection. Data flows provider → Meridian under both roles.
   */
  readonly direction: "inbound" | "outbound_control";
  /**
   * Whether starting this type does anything (REQ-PROVIDER-006).
   *
   * ``false`` marks a type that may be configured and persisted but has no
   * realization — assembly refuses it with an explicit not-implemented error.
   * The UI carries the same reasoning one step earlier: such an interface
   * renders read-only rather than offering controls that would fail at start.
   */
  readonly implemented: boolean;
  /**
   * Whether an interface of this type can only run through an installed
   * conversion profile (REQ-PROVIDER-040) — ``true`` for the ``native-*`` types,
   * whose decoder *is* the conversion engine.
   */
  readonly requiresConversionProfile: boolean;
  /**
   * The attributions an interface of this type may run (REQ-PROVIDER-028).
   *
   * Mirrors ``IngressInterfaceTypeDescriptor.allowed_trust_modes``. ``tunnel``
   * is absent from the ``native-*`` types at lean core — a native-format
   * provider without even transport identity has no attribution story — and the
   * CDS types carry only the static attribution their accredited link fixes.
   */
  readonly allowedTrustModes: readonly TrustMode[];
  /** The mode an interface of this type is born with. Always a member of the set above. */
  readonly defaultTrustMode: TrustMode;
  /**
   * The wire-security modes an interface of this type may run
   * (REQ-PROVIDER-029). Mirrors ``allowed_transport_security``.
   */
  readonly allowedTransportSecurity: readonly TransportSecurity[];
  /** The mode an interface of this type is born with. Always a member of the set above. */
  readonly defaultTransportSecurity: TransportSecurity;
}

/**
 * The three wire modes with their operator-facing meaning and consequence.
 *
 * ``consequence`` is non-empty only for ``plaintext``, and is the sentence
 * REQ-UI-161 requires **twice** — at selection and again at confirmation. It is
 * one constant so the two statements cannot drift into saying different things
 * about the same wire. Worded for *ingress*: data **from** a provider, not to a
 * peer.
 */
export const PROVIDER_TRANSPORT_SECURITY_MODES: readonly {
  value: TransportSecurity;
  label: string;
  hint: string;
  consequence: string | null;
}[] = [
  {
    value: "plaintext",
    label: "Plaintext — no TLS",
    hint: "No encryption and no authentication on the wire.",
    consequence: "Data from this provider is not encrypted or authenticated on the wire.",
  },
  {
    value: "tls",
    label: "TLS — provider authenticated",
    hint: "Meridian verifies the provider's chain and presents no certificate of its own.",
    consequence: null,
  },
  {
    value: "mtls",
    label: "mTLS — mutually authenticated",
    hint: "Both ends present and verify certificates. The fail-safe default.",
    consequence: null,
  },
];

/**
 * The two attributions with their operator-facing meaning.
 *
 * ``delegation`` is non-empty only for ``tunnel``, and is the whole of ADR-023
 * said once: it is what makes the ``producer_id`` caveat legible (REQ-PROVIDER-032
 * — a compromised upstream can claim any producer id, and the perimeter, not
 * Meridian, is what stopped it from being there at all).
 */
export const PROVIDER_TRUST_MODES: readonly {
  value: TrustMode;
  label: string;
  hint: string;
  delegation: string | null;
}[] = [
  {
    value: "mtls",
    label: "mTLS — Meridian authenticates the provider",
    hint: "The client is verified against this interface's provider CA bundle. The fail-safe default.",
    delegation: null,
  },
  {
    value: "tunnel",
    label: "Tunnel — the network layer authenticates",
    hint: "Static attribution inside a network-layer-protected path.",
    delegation:
      "Meridian does not authenticate this provider. The network layer admits the connection, " +
      "and every message on it is attributed to this provider.",
  },
];

/** Look up one wire mode's presentation, or ``null`` for a token this build does not know. */
export function findProviderTransportSecurity(
  mode: string,
): (typeof PROVIDER_TRANSPORT_SECURITY_MODES)[number] | null {
  return PROVIDER_TRANSPORT_SECURITY_MODES.find((m) => m.value === mode) ?? null;
}

/** Look up one attribution's presentation, or ``null`` for a token this build does not know. */
export function findTrustMode(mode: string): (typeof PROVIDER_TRUST_MODES)[number] | null {
  return PROVIDER_TRUST_MODES.find((m) => m.value === mode) ?? null;
}

/**
 * The **seven** ADR-040 §2.4 interface types, in the order the picker offers
 * them: the two direct types first (what an operator actually authors), then the
 * two conversion-backed ones, then the two CDS types — which normally arrive
 * **seeded** on a B/C install and are far less often created by hand — and the
 * reserved one last, because it is the only type that cannot be started.
 *
 * ⚠ ``allowedTransportSecurity`` on the two direct types is
 * ``["plaintext", "mtls"]``, **not all three**. That is the registry's set (its
 * descriptor default is ``frozenset({PLAINTEXT, MTLS})``) and its stated reason
 * is that a server-authenticated wire verifies no client and so attributes
 * nothing. Storyboard Flow V Frame 4's table says "all three"; the registry is
 * the code of record and offering ``tls`` here would be an affordance leading
 * straight to a 422.
 */
export const PROVIDER_INTERFACE_TYPES: readonly ProviderInterfaceTypeDescriptor[] = [
  {
    typeId: "sapient-data-ingress",
    label: "SAPIENT data",
    transportId: "mtls-tcp-listen",
    payloadDomain: "sapient",
    description: "BSI Flex 335 SAPIENT messages over framed TCP, from a Producer or Aggregator.",
    direction: "inbound",
    implemented: true,
    requiresConversionProfile: false,
    allowedTrustModes: ["mtls", "tunnel"],
    defaultTrustMode: "mtls",
    allowedTransportSecurity: ["plaintext", "mtls"],
    defaultTransportSecurity: "mtls",
  },
  {
    typeId: "raw-data-ingress",
    label: "Raw data",
    transportId: "mtls-tcp-listen",
    payloadDomain: "raw",
    description: "Raw sensor blobs, written to object storage before their metadata is published.",
    direction: "inbound",
    implemented: true,
    requiresConversionProfile: false,
    allowedTrustModes: ["mtls", "tunnel"],
    defaultTrustMode: "mtls",
    allowedTransportSecurity: ["plaintext", "mtls"],
    defaultTransportSecurity: "mtls",
  },
  {
    typeId: "native-data-ingress",
    label: "Native data",
    transportId: "mtls-tcp-listen",
    payloadDomain: "sapient",
    description:
      "A non-SAPIENT message format, decoded to canonical records by an installed conversion profile.",
    direction: "inbound",
    implemented: true,
    requiresConversionProfile: true,
    // A native-format provider without even transport identity has no
    // attribution story — tunnel returns only with a use case (ADR-040 §2.5).
    allowedTrustModes: ["mtls"],
    defaultTrustMode: "mtls",
    allowedTransportSecurity: ["mtls"],
    defaultTransportSecurity: "mtls",
  },
  {
    typeId: "native-raw-ingress",
    label: "Native raw",
    transportId: "mtls-tcp-listen",
    payloadDomain: "raw",
    description:
      "Non-SAPIENT raw payloads with a chunked announcement, likewise converted; the blob bytes stay uninterpreted.",
    direction: "inbound",
    implemented: true,
    requiresConversionProfile: true,
    allowedTrustModes: ["mtls"],
    defaultTrustMode: "mtls",
    allowedTransportSecurity: ["mtls"],
    defaultTransportSecurity: "mtls",
  },
  // ⚠ ONE CDS type, though ADR-040 §2.4 allocated two. The wire multiplexes every
  // content type onto a single socket, and on ingress a `listen` connection means
  // *Meridian binds* — so two interfaces could not both bind :5552. Architect ruling
  // 2026-08-27.
  //
  // `payloadDomain: "sapient"` although the interface also writes blobs: the domain
  // decides which filter clauses the builder offers, and the four canonical content
  // types on this wire genuinely carry alerts and classifications. Declaring "raw"
  // would silently withdraw those clauses.
  {
    typeId: "cds-ingress",
    label: "CDS",
    transportId: "cds-xml-tcp-listen",
    payloadDomain: "sapient",
    description:
      "The cross-domain receive path on the B/C side of the CDS link — status, " +
      "detection, alert and registration republished, raw-payload chunks reassembled.",
    direction: "inbound",
    implemented: true,
    requiresConversionProfile: false,
    // Raw TCP by the accredited transfer contract (ADR-005): the guard terminates
    // the wire, there is no TLS on it, and the counterparty is statically
    // attributed behind facility controls. Declared rather than left to
    // configuration so an operator cannot make the CDS interface unstartable by
    // "hardening" it.
    allowedTrustModes: ["tunnel"],
    defaultTrustMode: "tunnel",
    allowedTransportSecurity: ["plaintext"],
    defaultTransportSecurity: "plaintext",
  },
  // Reserved, not built (REQ-PROVIDER-006; ADR-040 §2.16 / V-10). Mirrored from
  // the registry rather than omitted, for the registry's own reason: an operator
  // who configures one gets an explicit "not implemented" instead of "unknown
  // type", which reads as a typo and invites a retry. An outbound control
  // channel toward providers needs its own ADR and re-accreditation before it
  // could ever be built.
  {
    typeId: "tasking-upstream",
    label: "Tasking upstream (reserved)",
    transportId: "mtls-tcp-listen",
    payloadDomain: "sapient",
    description: "RESERVED — upstream tasking toward providers. Configurable but not implemented.",
    direction: "outbound_control",
    implemented: false,
    requiresConversionProfile: false,
    allowedTrustModes: ["mtls"],
    defaultTrustMode: "mtls",
    allowedTransportSecurity: ["mtls"],
    defaultTransportSecurity: "mtls",
  },
];

/**
 * Look up a type descriptor, or ``null`` for one this build does not know.
 *
 * Null rather than a throw or a fabricated placeholder: an interface persisted
 * by a newer release must still be **listed** (its name and state are exactly
 * what an operator needs to stop it), so the surface degrades to showing the raw
 * ``type_id`` rather than hiding the row. On ingress that matters more than on
 * egress — a hidden ingress row is a feed nobody knows is running, and a stopped
 * one nobody knows is silent (CLAUDE.md §8.1).
 */
export function findProviderInterfaceType(typeId: string): ProviderInterfaceTypeDescriptor | null {
  return PROVIDER_INTERFACE_TYPES.find((descriptor) => descriptor.typeId === typeId) ?? null;
}

/**
 * Whether an interface of this type may be edited and started at all.
 *
 * Keyed on the descriptor's **declared flags** rather than on the ``type_id``
 * string (Flow V §5b), so a second reserved type needs no change here: today the
 * reserved type is exactly ``tasking-upstream``.
 *
 * An **unknown** type counts as reserved too: this build cannot know what its
 * fields mean, and offering an editor over a document it does not understand
 * risks saving back a lossy version of a live ingest path. It still *lists* —
 * never hide a running flow.
 */
export function isReservedProviderType(typeId: string): boolean {
  const descriptor = findProviderInterfaceType(typeId);
  return (
    descriptor === null || !descriptor.implemented || descriptor.direction === "outbound_control"
  );
}

/**
 * The types an operator may pick when **creating** an interface.
 *
 * Excludes the reserved ones: offering a type at create time that cannot then be
 * started would be an affordance leading nowhere. An interface already
 * configured with a reserved type still renders (read-only) — an existing one is
 * a fact about the deployment, and hiding it would be worse.
 */
export const CREATABLE_PROVIDER_INTERFACE_TYPES: readonly ProviderInterfaceTypeDescriptor[] =
  PROVIDER_INTERFACE_TYPES.filter((descriptor) => !isReservedProviderType(descriptor.typeId));

/**
 * The attributions an interface of ``typeId`` may run, in presentation order.
 *
 * An **unknown** type gets the full set rather than an empty one: the interface
 * still lists, and offering every mode lets the BFF's 422 be the authority
 * rather than this build's ignorance of a type a newer release added.
 */
export function allowedTrustModes(typeId: string): readonly TrustMode[] {
  const allowed = findProviderInterfaceType(typeId)?.allowedTrustModes;
  return PROVIDER_TRUST_MODES.filter((m) => allowed === undefined || allowed.includes(m.value)).map(
    (m) => m.value,
  );
}

/** The wire modes an interface of ``typeId`` may run. See {@link allowedTrustModes}. */
export function allowedProviderTransportSecurity(typeId: string): readonly TransportSecurity[] {
  const allowed = findProviderInterfaceType(typeId)?.allowedTransportSecurity;
  return PROVIDER_TRANSPORT_SECURITY_MODES.filter(
    (m) => allowed === undefined || allowed.includes(m.value),
  ).map((m) => m.value);
}

/**
 * Why a type's permitted **trust modes** are narrowed, in operator language — or
 * ``null`` where they are not narrowed and there is nothing to explain.
 *
 * REQ-UI-161 requires the reason to be *stated* wherever a value is absent. An
 * absence with no cause given is indistinguishable from a broken control.
 */
export function trustModeFixedReason(typeId: string): string | null {
  const descriptor = findProviderInterfaceType(typeId);
  if (descriptor === null || descriptor.allowedTrustModes.length > 1) return null;
  if (descriptor.transportId === "cds-xml-tcp-listen") {
    return "The CDS link is a facility-controlled guard path, so this interface type carries the static attribution its accredited contract fixes.";
  }
  if (descriptor.requiresConversionProfile) {
    return "A provider speaking a native format is authenticated by certificate; without transport identity there would be nothing to attribute its messages to.";
  }
  if (descriptor.direction === "outbound_control") {
    return "An outbound control channel toward a provider is mutually authenticated by definition; no weaker attribution is offered.";
  }
  // Reached only by a type whose set is narrowed for a reason this build has no
  // sentence for. Saying so is better than saying nothing — the operator learns
  // the constraint is real and that the UI cannot explain it, which is true.
  return "This interface type permits only the attribution shown.";
}

/**
 * Why a type's permitted **wire modes** are narrowed, or ``null`` where they are
 * not. See {@link trustModeFixedReason}.
 */
export function providerTransportSecurityFixedReason(typeId: string): string | null {
  const descriptor = findProviderInterfaceType(typeId);
  if (descriptor === null || descriptor.allowedTransportSecurity.length > 1) return null;
  if (descriptor.transportId === "cds-xml-tcp-listen") {
    return "The CDS link is raw TCP by the accredited transfer contract, so this interface type cannot run TLS or mTLS.";
  }
  if (descriptor.requiresConversionProfile || descriptor.direction === "outbound_control") {
    return "This interface type runs a mutually authenticated wire; no weaker mode is offered.";
  }
  return "This interface type permits only the mode shown.";
}

/**
 * The two connection roles with their operator-facing meaning (ADR-040 §2.5, V-2).
 *
 * ⚠ **Deliberately a different vocabulary from the egress
 * ``ConnectionRole``** (``pull`` / ``dial_out``). Data flows provider → Meridian
 * under both values; this names who *initiates the transport connection*. The
 * roles a direction can realize differ, and a shared name whose value set flips
 * per direction would be worse than two names.
 */
export const PROVIDER_CONNECTION_ROLES: readonly {
  value: ProviderConnectionRole;
  label: string;
  hint: string;
  /** Non-null where the role is model-present but has no transport (OQ-V-1). */
  unrealizedNote: string | null;
}[] = [
  {
    value: "listen",
    label: "Meridian listens",
    hint: "Meridian binds the port and the provider dials in. The default and the only realized role.",
    unrealizedNote: null,
  },
  {
    value: "dial_out",
    label: "Meridian dials the provider",
    hint: "Meridian opens the connection to the provider's host and port.",
    unrealizedNote:
      "No ingress transport realizes this role yet, so an interface configured with it is refused at start. It can be saved and inspected.",
  },
];

/** Look up one role's presentation, or ``null`` for a token this build does not know. */
export function findProviderConnectionRole(
  role: string,
): (typeof PROVIDER_CONNECTION_ROLES)[number] | null {
  return PROVIDER_CONNECTION_ROLES.find((r) => r.value === role) ?? null;
}

/**
 * The metadata key a provider projected out of the retired
 * ``control_plane.aggregators`` registry carries (migration 066).
 *
 * Named here rather than spelled inline at its one use site because it is a
 * **wire contract** with the migration: an aggregator row held identity and
 * trust facts only, never an endpoint or a type, so a projected provider has no
 * interfaces and would otherwise be indistinguishable from one whose interfaces
 * someone deleted (Flow V Frame 2).
 */
export const PROJECTED_FROM_AGGREGATOR_KEY = "projected_from_aggregator";
